import {
    Container,
    Row,
    Col,
    Form,
    InputGroup,
    FloatingLabel,
    Button,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import useUserApi from '@/shared/services/userApi';
import {
    validateImageFile,
    ACCEPTED_FILE_EXTENSIONS,
} from '@/shared/utils/imageValidation';
import { createAuthenticatedAxios } from '@/shared/services/axiosInstance';
import logger from '@/shared/utils/logger';
import sustainabilityHubLogo from '@/assets/img/logos/png/blueBackground.png';
import userIcon from '@/assets/img/icons/svg/userIcon.svg';
import peopleIcon from '@/assets/img/icons/svg/peopleIcon.svg';

import './CreateProfile.css';

const CreateProfile = () => {
    const [validated, setValidated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState(null);

    const [formData, setFormData] = useState({
        username: '',
        firstName: '',
        lastName: '',
        bio: '',
        userType: '',
        organization: '',
        interests: [],
        locations: [],
        externalLinks: [],
    });

    const [tempInputs, setTempInputs] = useState({
        interests: '',
        locations: '',
        externalLinks: '',
    });

    const { uploadProfilePicture } = useUserApi();

    const [touched, setTouched] = useState({
        username: false,
        firstName: false,
        lastName: false,
        bio: false,
        userType: false,
        organization: false,
        interests: false,
        locations: false,
        externalLinks: false,
    });

    const navigate = useNavigate();
    const { user, refreshProfileStatus } = useAuth();

    const capitalizeFirst = (str) => {
        if (!str) return '';
        return str
            .split(' ')
            .map(
                (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(' ');
    };

    const userTypes = [
        'student',
        'professor',
        'researcher',
        'industry',
        'government',
    ];

    const urlRegex = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i;

    const validate = (values) => {
        const errors = {};
        const nameRegex = /^[a-zA-Z-' ]{2,20}$/;
        const usernameRegex = /^[a-z0-9_-]{4,25}$/;

        if (!values.username.trim()) {
            errors.username = 'Username is required';
        } else if (!usernameRegex.test(values.username.trim())) {
            errors.username =
                'Username must be 4–25 characters: lowercase letters, numbers, _ or - only';
        }

        if (!values.firstName.trim()) {
            errors.firstName = 'First name is required';
        } else if (!nameRegex.test(values.firstName.trim())) {
            errors.firstName =
                'First name must be 2-20 characters and contain only letters, spaces, hyphens, and apostrophes';
        }

        if (!values.lastName.trim()) {
            errors.lastName = 'Last name is required';
        } else if (!nameRegex.test(values.lastName.trim())) {
            errors.lastName =
                'Last name must be 2-20 characters and contain only letters, spaces, hyphens, and apostrophes';
        }

        if (values.bio && values.bio.length > 500) {
            errors.bio = 'Bio must be less than 500 characters';
        }

        if (!values.userType) {
            errors.userType = 'Please select a user type';
        }

        if (values.organization && values.organization.trim().length < 2) {
            errors.organization =
                'Organization name must be at least 2 characters';
        }

        if (values.externalLinks && values.externalLinks.length > 0) {
            const invalid = values.externalLinks.filter(
                (u) => !urlRegex.test(u)
            );
            if (invalid.length) {
                errors.externalLinks =
                    'Some links are invalid (must start with http:// or https://)';
            }
        }

        return errors;
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const validationError = validateImageFile(file);
        if (validationError) {
            setErrors({ ...errors, profilePicture: validationError });
            return;
        }

        // Revoke previous object URL to prevent memory leak
        if (profilePicturePreview) {
            URL.revokeObjectURL(profilePicturePreview);
        }
        // clear errors and set file
        setErrors({ ...errors, profilePicture: null });
        setProfilePicture(file);
        setProfilePicturePreview(URL.createObjectURL(file));
    };

    const handleCreateProfile = async (event) => {
        event.preventDefault();
        const newErrors = validate(formData);
        setErrors(newErrors);

        setTouched({
            username: true,
            firstName: true,
            lastName: true,
            bio: true,
            userType: true,
            organization: true,
            interests: true,
            locations: true,
            externalLinks: true,
        });

        if (Object.keys(newErrors).length > 0) {
            setValidated(true);
            return;
        }

        if (!user) {
            navigate('/login');
            return;
        }
        setValidated(true);

        setLoading(true);

        try {
            const api = createAuthenticatedAxios(user);
            // Retrieve ToS acceptance stored during signup
            const tosAcceptedStr = localStorage.getItem('tosAccepted');
            let tosAccepted = null;
            if (tosAcceptedStr) {
                try {
                    tosAccepted = JSON.parse(tosAcceptedStr);
                } catch {
                    // Failed to parse ToS acceptance from localStorage - will use default values
                }
            }

            const profileData = {
                username: formData.username.trim(),
                name: {
                    first: formData.firstName,
                    last: formData.lastName,
                },
                email: user.email,
                bio: formData.bio,
                userType: formData.userType,
                organization: formData.organization,
                interests: formData.interests,
                locations: formData.locations,
                externalLinks: formData.externalLinks,
                // Include ToS acceptance from signup
                ...(tosAccepted && {
                    termsAndConditions: tosAccepted.termsAndConditions,
                    privacyPolicy: tosAccepted.privacyPolicy,
                }),
            };

            await api.post('/api/users', profileData);

            // Clear ToS acceptance from localStorage after successful profile creation
            localStorage.removeItem('tosAccepted');

            // Upload profile picture if one was selected
            if (profilePicture) {
                try {
                    const formData = new FormData();
                    formData.append('profilePicture', profilePicture);
                    await uploadProfilePicture(formData);
                } catch (uploadError) {
                    // Log error but don't fail the whole process
                    logger.error('Profile picture upload failed:', uploadError);
                }
            }

            // Refresh auth state through context
            refreshProfileStatus();
            navigate('/dashboard');
        } catch (error) {
            if (error.response?.status === 409) {
                const msg = error.response.data?.message || '';
                if (msg.toLowerCase().includes('username')) {
                    setErrors({
                        username:
                            'That username is already taken. Please choose another.',
                    });
                } else {
                    setErrors({
                        submit: 'A profile already exists for this account. Please use the Edit Profile feature instead.',
                    });
                }
            } else if (error.response?.data?.message) {
                setErrors({ submit: error.response.data.message });
            } else {
                setErrors({
                    submit: 'Profile creation failed. Please try again.',
                });
            }
            setValidated(false);

            logger.error('Account creation failed. Please try again. ', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddItem = (type, value) => {
        if (value.trim()) {
            // Validate external links before adding
            if (type === 'externalLinks' && !urlRegex.test(value.trim())) {
                // Invalid URL - show error, clear input, don't add badge
                // Keep existing errors (like submit errors) and add externalLinks error
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    externalLinks:
                        'Invalid link. Must start with http:// or https://',
                }));
                setValidated(true);
                setTempInputs({ ...tempInputs, externalLinks: '' });
                return;
            }

            if (!value) return;

            // Interests and locations are short free-text labels, so they are
            // constrained to letters/spaces/hyphens/apostrophes. External links
            // are URLs and were already validated above against urlRegex, so the
            // name-style fieldRegex must NOT be applied to them (a valid URL like
            // https://example.com fails fieldRegex and would be silently dropped).
            if (type !== 'externalLinks') {
                const fieldRegex = /^[a-zA-Z-' ]{2,20}$/;

                if (
                    formData[type].includes(capitalizeFirst(value)) ||
                    !fieldRegex.test(value.trim())
                ) {
                    return;
                }
            } else if (formData[type].includes(value.trim())) {
                // Avoid adding a duplicate link.
                return;
            }

            // External links are URLs and must be stored verbatim; capitalizing
            // them (Https://Example.Com) would corrupt the URL. Interests and
            // locations are display labels and are title-cased for consistency.
            const newItem =
                type === 'externalLinks'
                    ? value.trim()
                    : capitalizeFirst(value.trim());

            const updatedFormData = {
                ...formData,
                [type]: [...formData[type], newItem],
            };
            setFormData(updatedFormData);
            setTempInputs({ ...tempInputs, [type]: '' });
            setErrors(validate(updatedFormData));
        }
    };

    const handleRemoveItem = (type, index) => {
        const updatedFormData = {
            ...formData,
            [type]: formData[type].filter((_, i) => i !== index),
        };
        setFormData(updatedFormData);
        setErrors(validate(updatedFormData));
    };

    const handleKeyPress = (e, type) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddItem(type, tempInputs[type]);
        }
    };

    return (
        <div id="create-profile" className="bg-primary">
            <Container className="m-auto py-5">
                <Form
                    noValidate
                    onSubmit={handleCreateProfile}
                    className="create-profile-form d-flex flex-column align-items-center bg-white gap-3 mx-auto w-100 py-5 px-5"
                >
                    <Row className="mb-1">
                        <h1 className="h1 text-primary text-center">
                            Complete Your Profile
                        </h1>
                        {user?.email && (
                            <div className="text-muted text-center small mt-2">
                                Signed in as: {user.email}
                            </div>
                        )}
                    </Row>

                    {/* profile picture section */}
                    <Row className="mb-3 w-100">
                        <Col>
                            <div className="text-center">
                                <p className="text-muted mb-2">
                                    <strong>Profile Picture</strong> (Optional)
                                </p>
                                <p className="text-muted small">
                                    You can add or change this later in your
                                    profile settings.
                                </p>

                                {/* profile picture preview */}
                                <div className="mb-3">
                                    <img
                                        src={
                                            profilePicturePreview ||
                                            sustainabilityHubLogo
                                        }
                                        alt="Profile preview"
                                        className="profile-picture-preview"
                                    />
                                </div>

                                {/* file input */}
                                <div className="profile-picture-input-wrapper">
                                    <Form.Label
                                        htmlFor="profile-picture-input"
                                        className="visually-hidden"
                                    >
                                        Profile picture (JPEG, PNG, or HEIC, max
                                        5MB)
                                    </Form.Label>
                                    <Form.Control
                                        id="profile-picture-input"
                                        type="file"
                                        accept={ACCEPTED_FILE_EXTENSIONS}
                                        onChange={handleFileChange}
                                        disabled={loading}
                                        className="profile-picture-input"
                                        aria-describedby={
                                            errors.profilePicture
                                                ? 'profile-picture-error'
                                                : undefined
                                        }
                                    />
                                    {errors.profilePicture && (
                                        <div
                                            id="profile-picture-error"
                                            className="invalid-feedback d-block"
                                            role="alert"
                                        >
                                            {errors.profilePicture}
                                        </div>
                                    )}
                                    <Form.Text className="text-muted d-block mt-1 text-center">
                                        JPEG, PNG, or HEIC (max 5MB)
                                    </Form.Text>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Form.Group className="w-100 mb-3">
                        <InputGroup hasValidation className="input-logo-label">
                            <img src={userIcon} alt="" />
                            <FloatingLabel
                                controlId="floatingUsername"
                                label="Username *"
                            >
                                <Form.Control
                                    type="text"
                                    placeholder="Choose a username"
                                    value={formData.username}
                                    onChange={(e) => {
                                        const val =
                                            e.target.value.toLowerCase();
                                        const updatedForm = {
                                            ...formData,
                                            username: val,
                                        };
                                        setFormData(updatedForm);
                                        setErrors(validate(updatedForm));
                                    }}
                                    onBlur={() =>
                                        setTouched({
                                            ...touched,
                                            username: true,
                                        })
                                    }
                                    isInvalid={
                                        touched.username && !!errors.username
                                    }
                                    isValid={
                                        touched.username &&
                                        !errors.username &&
                                        formData.username.trim() !== ''
                                    }
                                    disabled={loading}
                                />
                            </FloatingLabel>
                        </InputGroup>
                        {touched.username && errors.username && (
                            <div className="invalid-feedback d-block fw-semibold ps-3">
                                {errors.username}
                            </div>
                        )}
                        <Form.Text className="text-muted ps-3">
                            4–25 characters. Letters, numbers, underscores, and
                            hyphens only.
                        </Form.Text>
                    </Form.Group>

                    <Row className="flex-xl-nowrap w-100">
                        <Form.Group as={Col} xl="6" className="mb-3">
                            <InputGroup
                                hasValidation
                                className="input-logo-label"
                            >
                                <img src={userIcon} alt="" />
                                <FloatingLabel
                                    controlId="floatingFirstName"
                                    label="First Name *"
                                >
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter your first name"
                                        value={formData.firstName}
                                        onChange={(e) => {
                                            const val = capitalizeFirst(
                                                e.target.value
                                            );
                                            const updatedForm = {
                                                ...formData,
                                                firstName: val,
                                            };
                                            setFormData(updatedForm);
                                            setErrors(validate(updatedForm));
                                        }}
                                        onBlur={() =>
                                            setTouched({
                                                ...touched,
                                                firstName: true,
                                            })
                                        }
                                        isInvalid={
                                            touched.firstName &&
                                            !!errors.firstName
                                        }
                                        isValid={
                                            touched.firstName &&
                                            !errors.firstName &&
                                            formData.firstName.trim() !== ''
                                        }
                                        disabled={loading}
                                    />
                                </FloatingLabel>
                            </InputGroup>
                            {validated && errors.firstName && (
                                <div className="invalid-feedback d-block fw-semibold ps-3">
                                    {errors.firstName}
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group as={Col} xl="6" className="mb-3">
                            <InputGroup
                                hasValidation
                                className="input-logo-label"
                            >
                                <img src={userIcon} alt="" />
                                <FloatingLabel
                                    controlId="floatingLastName"
                                    label="Last Name *"
                                >
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter your last name"
                                        value={formData.lastName}
                                        onChange={(e) => {
                                            const val = capitalizeFirst(
                                                e.target.value
                                            );
                                            const updatedForm = {
                                                ...formData,
                                                lastName: val,
                                            };
                                            setFormData(updatedForm);
                                            setErrors(validate(updatedForm));
                                        }}
                                        onBlur={() =>
                                            setTouched({
                                                ...touched,
                                                lastName: true,
                                            })
                                        }
                                        isInvalid={
                                            touched.lastName &&
                                            !!errors.lastName
                                        }
                                        isValid={
                                            touched.lastName &&
                                            !errors.lastName &&
                                            formData.lastName.trim() !== ''
                                        }
                                        disabled={loading}
                                    />
                                </FloatingLabel>
                            </InputGroup>
                            {validated && errors.lastName && (
                                <div className="invalid-feedback d-block fw-semibold ps-3">
                                    {errors.lastName}
                                </div>
                            )}
                        </Form.Group>
                    </Row>

                    <Form.Group className="w-100 mb-3">
                        <InputGroup hasValidation className="input-logo-label">
                            <img src={peopleIcon} alt="" />
                            <FloatingLabel
                                controlId="floatingUserType"
                                label="User Type *"
                            >
                                <Form.Select
                                    value={formData.userType}
                                    onChange={(e) => {
                                        const updatedForm = {
                                            ...formData,
                                            userType: e.target.value,
                                        };
                                        setFormData(updatedForm);
                                        setErrors(validate(updatedForm));
                                    }}
                                    onBlur={() =>
                                        setTouched({
                                            ...touched,
                                            userType: true,
                                        })
                                    }
                                    isInvalid={
                                        touched.userType && !!errors.userType
                                    }
                                    isValid={
                                        touched.userType &&
                                        !errors.userType &&
                                        formData.userType !== ''
                                    }
                                    disabled={loading}
                                >
                                    <option value="">Select user type</option>
                                    {userTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type.charAt(0).toUpperCase() +
                                                type.slice(1)}
                                        </option>
                                    ))}
                                </Form.Select>
                            </FloatingLabel>
                        </InputGroup>
                        {validated && errors.userType && (
                            <div className="invalid-feedback d-block fw-semibold ps-3">
                                {errors.userType}
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group className="w-100 mb-3" controlId="profileBio">
                        <Form.Label className="fw-semibold">
                            Bio (Optional)
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            placeholder="Tell us about yourself"
                            value={formData.bio}
                            onChange={(e) => {
                                const updatedForm = {
                                    ...formData,
                                    bio: e.target.value,
                                };
                                setFormData(updatedForm);
                                setErrors(validate(updatedForm));
                            }}
                            onBlur={() =>
                                setTouched({
                                    ...touched,
                                    bio: true,
                                })
                            }
                            isInvalid={touched.bio && !!errors.bio}
                            isValid={
                                touched.bio &&
                                !errors.bio &&
                                formData.bio.trim() !== ''
                            }
                            disabled={loading}
                        />
                        {validated && errors.bio && (
                            <div className="invalid-feedback d-block fw-semibold ps-3">
                                {errors.bio}
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group
                        className="w-100 mb-3"
                        controlId="profileOrganization"
                    >
                        <Form.Label className="fw-semibold">
                            Organization (Optional)
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter organization"
                            value={formData.organization}
                            onChange={(e) => {
                                const updatedForm = {
                                    ...formData,
                                    organization: e.target.value,
                                };
                                setFormData(updatedForm);
                                setErrors(validate(updatedForm));
                            }}
                            onBlur={() =>
                                setTouched({
                                    ...touched,
                                    organization: true,
                                })
                            }
                            isInvalid={
                                touched.organization && !!errors.organization
                            }
                            isValid={
                                touched.organization &&
                                !errors.organization &&
                                formData.organization.trim() !== ''
                            }
                            disabled={loading}
                        />
                        {validated && errors.organization && (
                            <div className="invalid-feedback d-block fw-semibold ps-3">
                                {errors.organization}
                            </div>
                        )}
                    </Form.Group>

                    {/* Interests */}
                    <Form.Group
                        className="w-100 mb-3"
                        controlId="profileInterests"
                    >
                        <Form.Label className="fw-semibold">
                            Interests (Optional)
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Add an interest..."
                            value={tempInputs.interests}
                            onChange={(e) =>
                                setTempInputs({
                                    ...tempInputs,
                                    interests: e.target.value,
                                })
                            }
                            onKeyDown={(e) => handleKeyPress(e, 'interests')}
                            onBlur={() => {
                                if (tempInputs.interests.trim()) {
                                    handleAddItem(
                                        'interests',
                                        tempInputs.interests
                                    );
                                }
                                setTouched({
                                    ...touched,
                                    interests: true,
                                });
                            }}
                            disabled={loading}
                        />
                        <div className="mt-2 d-flex flex-wrap gap-2">
                            {formData.interests.map((interest, index) => (
                                <span
                                    key={index}
                                    className="badge bg-primary d-flex align-items-center gap-1"
                                >
                                    {interest}
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() =>
                                            handleRemoveItem('interests', index)
                                        }
                                        aria-label={`Remove ${interest}`}
                                        disabled={loading}
                                    />
                                </span>
                            ))}
                        </div>
                    </Form.Group>

                    {/* Locations */}
                    <Form.Group
                        className="w-100 mb-3"
                        controlId="profileLocations"
                    >
                        <Form.Label className="fw-semibold">
                            Location (Optional)
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Add a Colorado city or region..."
                            value={tempInputs.locations}
                            onChange={(e) =>
                                setTempInputs({
                                    ...tempInputs,
                                    locations: e.target.value,
                                })
                            }
                            onKeyDown={(e) => handleKeyPress(e, 'locations')}
                            onBlur={() => {
                                if (tempInputs.locations.trim()) {
                                    handleAddItem(
                                        'locations',
                                        tempInputs.locations
                                    );
                                }
                                setTouched({
                                    ...touched,
                                    locations: true,
                                });
                            }}
                            disabled={loading}
                        />
                        <div className="mt-2 d-flex flex-wrap gap-2">
                            {formData.locations.map((location, index) => (
                                <span
                                    key={index}
                                    className="badge bg-primary d-flex align-items-center gap-1"
                                >
                                    {location}
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() =>
                                            handleRemoveItem('locations', index)
                                        }
                                        aria-label={`Remove ${location}`}
                                        disabled={loading}
                                    />
                                </span>
                            ))}
                        </div>
                    </Form.Group>

                    {/* External Links */}
                    <Form.Group
                        className="w-100 mb-3"
                        controlId="profileExternalLinks"
                    >
                        <Form.Label className="fw-semibold">
                            External Links (Optional)
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Add a link (must start with http:// or https://)..."
                            value={tempInputs.externalLinks}
                            onChange={(e) =>
                                setTempInputs({
                                    ...tempInputs,
                                    externalLinks: e.target.value,
                                })
                            }
                            onKeyDown={(e) =>
                                handleKeyPress(e, 'externalLinks')
                            }
                            onBlur={() => {
                                if (tempInputs.externalLinks.trim()) {
                                    handleAddItem(
                                        'externalLinks',
                                        tempInputs.externalLinks
                                    );
                                }
                                setTouched({
                                    ...touched,
                                    externalLinks: true,
                                });
                            }}
                            disabled={loading}
                        />
                        <div className="mt-2 d-flex flex-wrap gap-2">
                            {formData.externalLinks.map((link, index) => (
                                <span
                                    key={index}
                                    className="badge bg-primary d-flex align-items-center gap-1"
                                >
                                    {link}
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() =>
                                            handleRemoveItem(
                                                'externalLinks',
                                                index
                                            )
                                        }
                                        aria-label={`Remove ${link}`}
                                        disabled={loading}
                                    />
                                </span>
                            ))}
                        </div>
                        {validated && errors.externalLinks && (
                            <div className="invalid-feedback d-block fw-semibold ps-3">
                                {errors.externalLinks}
                            </div>
                        )}
                    </Form.Group>

                    {errors.submit &&
                        !errors.username &&
                        !errors.firstName &&
                        !errors.lastName &&
                        !errors.userType &&
                        !errors.externalLinks &&
                        !errors.bio &&
                        !errors.organization && (
                            <div
                                className="text-danger text-center fw-semibold mb-3"
                                role="alert"
                            >
                                {errors.submit}
                            </div>
                        )}

                    <Row className="mt-4 mx-auto">
                        <Button
                            className="signup-btn btn-lg bg-primary text-white text-center fw-bold m-auto py-3"
                            type="submit"
                            disabled={loading}
                        >
                            {loading
                                ? 'Creating Profile...'
                                : 'Complete Profile'}
                        </Button>
                    </Row>
                </Form>
            </Container>
        </div>
    );
};

export default CreateProfile;
